import { useEffect } from 'react';

import type React from 'react';

export function useOutsideAlerter(
  ref: React.RefObject<any>,
  action: (event: MouseEvent) => void,
) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target)) {
        action(event);
      }
    }

    /**
     * Detect clicks inside iframes by monitoring when the window loses focus.
     * This handles the case where clicks inside an iframe don't bubble to the parent document.
     */
    function handleWindowBlur() {
      // Check if the active element is an iframe
      if (
        document.activeElement &&
        document.activeElement.tagName === 'IFRAME'
      ) {
        // Treat iframe focus as an outside click
        if (ref.current && !ref.current.contains(document.activeElement)) {
          // Create a synthetic event for consistency
          const syntheticEvent = new MouseEvent('mousedown', {
            bubbles: true,
            cancelable: true,
            view: window,
          });
          action(syntheticEvent);
        }
      }
    }

    // Bind the event listeners
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('blur', handleWindowBlur);

    return () => {
      // Unbind the event listeners on clean up
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [ref, action]);
}
